import React, { useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import Modal from "@/components/ui/modal";
import { Filter, RotateCcw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEFAULT_FILTERS,
  DEFAULT_WITH,
  WITH_OPTIONS,
} from "../repairConstants";

const FilterRepairModalContent = ({
  show,
  onHide,
  categories = [],
  filters,
  setFilters,
  technicians = [],
  stores = [],
  currentStore = null,
  pageSize = 10,
  onPageSizeChange,
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [localPageSize, setLocalPageSize] = useState(pageSize || 10);

  const handleApply = () => {
    setFilters(localFilters);
    if (onPageSizeChange && localPageSize !== pageSize) {
      onPageSizeChange(localPageSize);
    }
    onHide();
  };

  const handleReset = () => {
    setLocalFilters(DEFAULT_FILTERS);
    setLocalPageSize(10);
    setFilters(DEFAULT_FILTERS);
    if (onPageSizeChange) {
      onPageSizeChange(10);
    }
    onHide();
  };

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title="Filter Repair Jobs"
      icon={Filter}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="outline" className="gap-2" onClick={handleReset}>
            <RotateCcw size={16} /> Reset Filters
          </Button>
          <Button className="gap-2" onClick={handleApply}>
            <Check size={16} /> Apply Filters
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-1">
        {/* Categories (repair_category_ids: array[integer]) */}
        <div className="md:col-span-2 space-y-2">
          <Label className="font-medium">Categories</Label>
          <Select
            isMulti
            classNamePrefix="react-select"
            options={categories.map((cat) => ({
              value: Number(cat.id),
              label: cat.name,
            }))}
            value={categories
              .filter((cat) =>
                (localFilters.repair_category_ids || []).includes(Number(cat.id))
              )
              .map((cat) => ({
                value: Number(cat.id),
                label: cat.name,
              }))}
            onChange={(options) =>
              setLocalFilters({
                ...localFilters,
                repair_category_ids: options
                  ? options.map((o) => Number(o.value)).filter((n) => !isNaN(n))
                  : [],
              })
            }
            placeholder="Select Categories"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "40px",
                borderColor: "hsl(var(--input))",
                borderRadius: "calc(var(--radius) - 2px)",
              }),
            }}
          />
        </div>

        {/* Status (status: pending, in_progress, awaiting_parts, completed, cancelled) */}
        <div className="space-y-2">
          <Label className="font-medium">Status</Label>
          <ShadcnSelect
            value={localFilters.status || "all"}
            onValueChange={(val) =>
              setLocalFilters({
                ...localFilters,
                status: val === "all" ? "" : val,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="awaiting_parts">Awaiting Parts</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Priority (priority: low, normal, high, urgent) */}
        <div className="space-y-2">
          <Label className="font-medium">Priority</Label>
          <ShadcnSelect
            value={localFilters.priority || "all"}
            onValueChange={(val) =>
              setLocalFilters({
                ...localFilters,
                priority: val === "all" ? "" : val,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Technician (technician_id: uuid) */}
        <div className="space-y-2">
          <Label className="font-medium">Technician</Label>
          <ShadcnSelect
            value={localFilters.technician_id?.toString() || "all"}
            onValueChange={(val) =>
              setLocalFilters({
                ...localFilters,
                technician_id: val === "all" ? "" : val,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Technicians" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Technicians</SelectItem>
              {technicians.map((tech) => (
                <SelectItem key={tech.id} value={tech.id.toString()}>
                  {tech.full_name || tech.name}
                </SelectItem>
              ))}
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Store (store_id: uuid) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="font-medium">Store</Label>
            {currentStore?.id &&
              localFilters.store_id?.toString() !==
              currentStore.id.toString() && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline font-medium"
                  onClick={() =>
                    setLocalFilters({
                      ...localFilters,
                      store_id: currentStore.id.toString(),
                    })
                  }
                >
                  Use Current Store
                </button>
              )}
          </div>
          <ShadcnSelect
            value={localFilters.store_id?.toString() || "all"}
            onValueChange={(val) =>
              setLocalFilters({
                ...localFilters,
                store_id: val === "all" ? "" : val,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores (No Filter)</SelectItem>
              {stores.map((store) => (
                <SelectItem key={store.id} value={store.id.toString()}>
                  {store.name}
                  {currentStore?.id &&
                    store.id.toString() === currentStore.id.toString()
                    ? " (Current Store)"
                    : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Date From (date_from: string<date-time>) */}
        <div className="space-y-2">
          <Label className="font-medium">Date From</Label>
          <Input
            type="datetime-local"
            value={localFilters.date_from || ""}
            onChange={(e) =>
              setLocalFilters({ ...localFilters, date_from: e.target.value })
            }
          />
        </div>

        {/* Date To (date_to: string<date-time>) */}
        <div className="space-y-2">
          <Label className="font-medium">Date To</Label>
          <Input
            type="datetime-local"
            value={localFilters.date_to || ""}
            onChange={(e) =>
              setLocalFilters({ ...localFilters, date_to: e.target.value })
            }
          />
        </div>

        {/* Sort By (sort_by: created_at, repair_number, priority, status, estimated_completion_date, total_cost) */}
        <div className="space-y-2">
          <Label className="font-medium">Sort By</Label>
          <ShadcnSelect
            value={localFilters.sort_by || "created_at"}
            onValueChange={(val) =>
              setLocalFilters({ ...localFilters, sort_by: val })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Created Date</SelectItem>
              <SelectItem value="repair_number">Repair Number</SelectItem>
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="status">Status</SelectItem>
              <SelectItem value="estimated_completion_date">
                Completion Date
              </SelectItem>
              <SelectItem value="total_cost">Total Cost</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Sort Direction (sort_direction: asc, desc) */}
        <div className="space-y-2">
          <Label className="font-medium">Direction</Label>
          <ShadcnSelect
            value={localFilters.sort_direction || "desc"}
            onValueChange={(val) =>
              setLocalFilters({ ...localFilters, sort_direction: val })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Descending</SelectItem>
              <SelectItem value="asc">Ascending</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Per Page (per_page: integer >= 1 <= 100) */}
        <div className="space-y-2">
          <Label className="font-medium">Per Page</Label>
          <ShadcnSelect
            value={localPageSize?.toString() || "10"}
            onValueChange={(val) => setLocalPageSize(Number(val))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 per page</SelectItem>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        </div>

        {/* Include Relations (with: array[string]) */}
        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="font-medium">Include Relations (with)</Label>
            <button
              type="button"
              className="text-xs text-primary hover:underline font-medium"
              onClick={() => {
                const currentWith = localFilters.with || [];
                const allSelected = currentWith.length === WITH_OPTIONS.length;
                setLocalFilters({
                  ...localFilters,
                  with: allSelected ? [] : DEFAULT_WITH,
                });
              }}
            >
              {(localFilters.with || []).length === WITH_OPTIONS.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
          <Select
            isMulti
            classNamePrefix="react-select"
            options={WITH_OPTIONS}
            value={WITH_OPTIONS.filter((opt) =>
              (localFilters.with || DEFAULT_WITH).includes(opt.value)
            )}
            onChange={(options) =>
              setLocalFilters({
                ...localFilters,
                with: options ? options.map((o) => o.value) : [],
              })
            }
            placeholder="Select relations to include"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "40px",
                borderColor: "hsl(var(--input))",
                borderRadius: "calc(var(--radius) - 2px)",
              }),
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

FilterRepairModalContent.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  categories: PropTypes.array,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  technicians: PropTypes.array,
  stores: PropTypes.array,
  currentStore: PropTypes.object,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
};

const FilterRepairModal = (props) => {
  if (!props.show) return null;
  return <FilterRepairModalContent {...props} />;
};

FilterRepairModal.propTypes = {
  show: PropTypes.bool.isRequired,
};

export default FilterRepairModal;
